import {
  addDays,
  endOfWeek,
  endOfYear,
  format,
  isAfter,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns'

export interface OrderDateRange {
  from: string
  to: string
}

export interface OrderDateApiFilter {
  created_at_from: string
  created_at_to: string
}

export type OrderDatePresetId =
  | 'all'
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'this-year'
  | 'last-year'

export interface OrderDatePreset {
  id: OrderDatePresetId
  label: string
}

export const orderDatePresets: OrderDatePreset[] = [
  { id: 'all', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this-week', label: 'This Week' },
  { id: 'last-week', label: 'Last Week' },
  { id: 'this-month', label: 'This Month' },
  { id: 'last-month', label: 'Last Month' },
  { id: 'this-year', label: 'This Year' },
  { id: 'last-year', label: 'Last Year' },
]

export const toDateKey = (date: Date) => format(startOfDay(date), 'yyyy-MM-dd')

export const parseDateKey = (value: string) => {
  const parsedDate = parseISO(value)
  return isValid(parsedDate) ? startOfDay(parsedDate) : null
}

export const isOrderDateRange = (value: unknown): value is OrderDateRange => {
  if (!value || typeof value !== 'object') return false

  const range = value as Partial<OrderDateRange>
  const from = typeof range.from === 'string' ? parseDateKey(range.from) : null
  const to = typeof range.to === 'string' ? parseDateKey(range.to) : null

  return Boolean(from && to && !isAfter(from, to))
}

const createRange = (from: Date, to: Date): OrderDateRange => ({
  from: toDateKey(from),
  to: toDateKey(to),
})

export const getPresetRange = (presetId: OrderDatePresetId, now = new Date()): OrderDateRange | null => {
  const today = startOfDay(now)

  switch (presetId) {
    case 'all':
      return null
    case 'today':
      return createRange(today, today)
    case 'yesterday': {
      const yesterday = subDays(today, 1)
      return createRange(yesterday, yesterday)
    }
    case 'this-week':
      return createRange(startOfWeek(today, { weekStartsOn: 1 }), today)
    case 'last-week': {
      const lastWeek = subWeeks(today, 1)
      return createRange(startOfWeek(lastWeek, { weekStartsOn: 1 }), endOfWeek(lastWeek, { weekStartsOn: 1 }))
    }
    case 'this-month':
      return createRange(startOfMonth(today), today)
    case 'last-month': {
      const lastMonth = subMonths(today, 1)
      return createRange(startOfMonth(lastMonth), new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0))
    }
    case 'this-year':
      return createRange(startOfYear(today), today)
    case 'last-year': {
      const lastYear = subYears(today, 1)
      return createRange(startOfYear(lastYear), endOfYear(lastYear))
    }
  }
}

export const getMatchingPreset = (range: OrderDateRange | null, now = new Date()): OrderDatePreset | null => {
  if (!range) return orderDatePresets[0]

  return (
    orderDatePresets.slice(1).find((preset) => {
      const presetRange = getPresetRange(preset.id, now)
      return presetRange?.from === range.from && presetRange.to === range.to
    }) || null
  )
}

// Formats a Date as a UTC RFC3339 timestamp without milliseconds
// (e.g. "2026-07-22T04:00:00Z") — the format the backend's parseDateQuery reads.
const toUtcRfc3339 = (date: Date): string => date.toISOString().replace(/\.\d{3}Z$/, 'Z')

// Converts an inclusive local-day range (`from`..`to`, both days included) into
// the half-open UTC interval the API expects. Each bound is a LOCAL-midnight
// boundary converted to a UTC instant, so the customer's timezone is baked into
// the timestamp:
//   created_at_from = start of the first local day
//   created_at_to   = start of the day AFTER `to` (exclusive)
// e.g. Today in Toronto (UTC-4) -> [2026-07-22T04:00:00Z, 2026-07-23T04:00:00Z).
// Applies to every preset and custom range, not just Today.
export const toOrderDateApiFilter = (range: OrderDateRange): OrderDateApiFilter => {
  const from = parseDateKey(range.from)
  const to = parseDateKey(range.to)
  return {
    created_at_from: from ? toUtcRfc3339(from) : range.from,
    created_at_to: to ? toUtcRfc3339(addDays(to, 1)) : range.to,
  }
}

export const getDateFilterSubtitle = (range: OrderDateRange | null) => {
  if (!range) return null
  return getMatchingPreset(range)?.label || 'Custom Date'
}
